import { FC } from 'react';

import { EAssetType } from 'constants/enums';
import {
  DEFAULT_BORDER_COLOR,
  PROGRESS_BAR_DEFAULTS
} from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignFormikProps } from 'views/OfferDesign/OfferDesign.types';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import Input from 'components/Input/Input';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import { Separator } from 'components/ui/separator';
import UploadImage from 'components/UploadImage/UploadImage';

// TO BE REMOVED WHEN NEW COMPONENT IS AVAILABLE
const GRADIENT_INPUT_STYLE =
  '[&_.formContent-input-fieldTitle]:!mb-[8px] [&_.formContent-input-fieldTitle_h3]:!text-sm [&_.formContent-input-fieldTitle_h3]:!font-medium [&_.MuiInputBase-root]:!h-10 [&_.MuiInputBase-root]:!text-sm [&_.MuiInputBase-root]:!py-0 [&_.MuiOutlinedInput-root]:!rounded-md [&_.MuiInputAdornment-root_div]:!h-6 [&_.MuiInputAdornment-root_div]:!w-6';

type ProgressBarFormFieldsProps = OfferDesignFormikProps & {
  isNameUnique: boolean;
  isExternalIdUnique: boolean;
};

export const ProgressBarFormFields: FC<ProgressBarFormFieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  isNameUnique,
  isExternalIdUnique
}) => {
  const nameError = touched.name && errors.name;
  const nameUniqueError =
    !isNameUnique && touched.name ? 'Name already exists' : '';
  const externalIdError = touched.externalId && errors.externalId;
  const externalIdUniqueError =
    !isExternalIdUnique && touched.externalId
      ? 'External ID already exists'
      : '';

  return (
    <div className="rounded-lg border border-base-border p-9">
      <h3 className="text-[20px] font-semibold text-zinc-900">Progress Bar</h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Customize the progress bar to fit your store theme.
      </p>

      <div className="flex gap-5">
        <Input
          label="Name"
          isRequired
          name="name"
          placeholder="Name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          state={nameError || nameUniqueError ? 'error' : 'default'}
          description={(nameError as string) || nameUniqueError || undefined}
          className="flex-1"
        />
        <Input
          label="External ID"
          isRequired
          name="externalId"
          placeholder="External ID"
          value={values.externalId}
          onChange={handleChange}
          onBlur={handleBlur}
          state={
            externalIdError || externalIdUniqueError ? 'error' : 'default'
          }
          description={
            (externalIdError as string) || externalIdUniqueError || undefined
          }
          className="flex-1"
        />
      </div>

      <Separator className="mt-5 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900">
        Progress Bar Frame
      </h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Set your frame settings.
      </p>

      <div className="flex gap-5">
        <Input
          label="Background Opacity"
          isRequired
          name="backgroundOpacity"
          type="number"
          min={0}
          max={100}
          value={values.backgroundOpacity}
          onChange={handleChange}
          onBlur={handleBlur}
          leftIcon={<span>%</span>}
          leftIconStyles="text-base-foreground text-p-s absolute left-2 top-1/2 -translate-y-1/2"
          className="flex-1"
        />
        <Input
          label="Frame Radius"
          isRequired
          name="frameRadius"
          type="number"
          min={0}
          value={values.frameRadius}
          onChange={handleChange}
          onBlur={handleBlur}
          className="flex-1"
        />
      </div>

      <Separator className="mt-5 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900">
        Progress Bar Design
      </h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Set colors and upload assets for your bar.
      </p>

      <div className="flex gap-5">
        <div className="flex-1">
          <ColorPicker
            label="Font Color"
            defaultValue={values.textColor || PROGRESS_BAR_DEFAULTS.textColor}
            onChange={(color) => setFieldValue('textColor', color)}
          />
        </div>
        {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
        <div className={`flex-1 ${GRADIENT_INPUT_STYLE}`}>
          <AcGradientInput
            header="Bar Fill Color"
            name="barFillColor"
            defaultValue={
              typeof values.barFillColor === 'object' && values.barFillColor
                ? values.barFillColor
                : { colorOne: values.barFillColor || PROGRESS_BAR_DEFAULTS.barFillColor.colorOne }
            }
            onChange={handleChange}
            setValue={setFieldValue}
            onBlur={handleBlur}
          />
        </div>
      </div>

      {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
      <div className="flex gap-5 mt-4">
        <div className={`flex-1 ${GRADIENT_INPUT_STYLE}`}>
          <AcGradientInput
            header="Border Color"
            name="borderColor"
            defaultValue={
              typeof values.borderColor === 'object' && values.borderColor
                ? values.borderColor
                : DEFAULT_BORDER_COLOR
            }
            onChange={handleChange}
            setValue={setFieldValue}
            onBlur={handleBlur}
          />
        </div>
        <div className={`flex-1 ${GRADIENT_INPUT_STYLE}`}>
          <AcGradientInput
            header="Background Color"
            name="barBackgroundColor"
            defaultValue={
              typeof values.barBackgroundColor === 'object' && values.barBackgroundColor
                ? values.barBackgroundColor
                : PROGRESS_BAR_DEFAULTS.backgroundColor
            }
            onChange={handleChange}
            setValue={setFieldValue}
            onBlur={handleBlur}
          />
        </div>
      </div>

      <div className="flex gap-5 mt-4">
        <Input
          label="Bar Radius"
          name="barRadius"
          type="number"
          min={0}
          value={values.barRadius}
          onChange={handleChange}
          onBlur={handleBlur}
          className="flex-1"
        />
        <Input
          label="Border Width"
          name="borderWidth"
          type="number"
          min={0}
          value={values.borderWidth}
          onChange={handleChange}
          onBlur={handleBlur}
          className="flex-1"
        />
      </div>

      <div className="mt-9">
        <UploadImage
          title="Accumulated Image"
          isRequired
          className="w-full !flex flex-row px-5"
          onUpload={(url) => setFieldValue('accumulatedItemImageUrl', url)}
          selectedImageUrl={values.accumulatedItemImageUrl}
          forceType={EAssetType.BG_BUNDLE}
          TooltipContent={() => (
            <span>Upload an accumulated item image for the progress bar</span>
          )}
        />
      </div>
    </div>
  );
};
